import { Sparkles, Loader2, RefreshCcw, CalendarDays, Download, Bell } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { getGrowthDashboard, refreshGrowthDashboard, updateSavedMessageMetadata, getPlanPreferences, updatePlanPreferences } from '../lib/api';
import { useUsageSummary } from '../hooks/useUsageSummary';
import { exportUserData } from '../lib/exportData';
import InsightsV2 from '../components/insights/InsightsV2';

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (err) {
    return value as any;
  }
};

const formatRelative = (value: string | null | undefined) => {
  if (!value) return 'No recent refresh';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs)) return formatDateTime(value);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return formatDateTime(value);
};

export default function InsightsPageV2() {
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  useUsageSummary();
  const [prefs, setPrefs] = useState<any | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState<boolean>(false);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['growth-dashboard'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return getGrowthDashboard(token);
    },
    enabled: isSignedIn,
    refetchOnMount: true,
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return refreshGrowthDashboard(token);
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(['growth-dashboard'], payload);
    },
    onError: (error: any) => {
      if (error?.message?.includes('daily_limit_exceeded')) {
        alert('You can refresh insights up to twice every 24 hours. Please try again later.');
      } else {
        alert('Failed to refresh insights. Please try again.');
      }
    },
  });

  const focusMutation = useMutation({
    mutationFn: async (messageId: string) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return updateSavedMessageMetadata({ id: messageId, priority: 'high', timeframe: 'this week', status: 'todo' }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['growth-dashboard'] });
    },
    onError: () => {
      alert('Failed to set current focus. Please try again.');
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const p = await getPlanPreferences(token);
        setPrefs(p);
      } catch {}
    })();
  }, [getToken]);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const u = sp.get('updated');
    const m = sp.get('manage');
    if (u) {
      const msg = u === 'done' ? 'Marked as Done' : u === 'in_progress' ? 'Marked as In Progress' : u === 'snoozed' ? 'Snoozed for 1 week' : null;
      if (msg) setFlash(msg);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (m === 'followups') {
      setManageOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleExportAnalytics = async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      await exportUserData(token);
    } catch (e) {
      alert('Failed to export analytics data. Please try again.');
    }
  };

  const refreshLabel = refreshMutation.isPending ? 'Refreshing…' : 'Refresh from latest MoMs';

  if (isLoading) {
    return (
      <AppLayout currentPage="insights-hub">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-royal-blue mx-auto" />
            <p className="text-slate-600 font-display">Preparing your action plan…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !data) {
    return (
      <AppLayout currentPage="insights-hub">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-red-100 shadow-sm text-center space-y-4">
            <Sparkles className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-display font-bold text-slate-900">Unable to load insights</h2>
            <p className="text-slate-600">{(error as Error)?.message || 'Something went wrong while fetching your analytics.'}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="insights-hub">
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <section className="rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-royal-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-royal-blue">
                  <Sparkles className="h-3.5 w-3.5" />
                  Action-First Insights
                </div>
                <h1 className="font-display text-3xl font-semibold text-slate-900 md:text-4xl">
                  Your 7-Day Action Plan
                </h1>
                <p className="text-base text-slate-600 max-w-2xl">
                  Immediate clarity: your single priority, 2–3 actions, and a quick progress snapshot.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
                  Source: {data.analysis_source === 'mom_summaries' ? 'MoM summaries' : data.analysis_source === 'conversation_messages' ? 'Chat transcripts' : 'Unknown'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const token = await getToken();
                        if (!token || !prefs) return;
                        const next = { ...prefs, coaching_reminders: !prefs.coaching_reminders };
                        await updatePlanPreferences(next, token);
                        setPrefs(next);
                      } catch {}
                    }}
                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-display font-semibold shadow-sm transition border ${prefs?.coaching_reminders ? 'bg-emerald-500 !text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                    title="Toggle follow-up reminders"
                  >
                    <Bell className="h-4 w-4" /> {prefs?.coaching_reminders ? 'Follow-ups On' : 'Follow-ups Off'}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportAnalytics}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-display font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    title="Download analytics data for analysis"
                  >
                    <Download className="h-4 w-4" />
                    Export Data
                  </button>
                  <div className="relative group">
                    <button
                      type="button"
                      onClick={() => refreshMutation.mutate()}
                      disabled={refreshMutation.isPending || isFetching}
                      className="inline-flex items-center gap-2 rounded-2xl bg-royal-blue px-4 py-2 text-sm font-display font-semibold !text-white shadow-sm transition hover:bg-royal-blue/90 disabled:opacity-60"
                      title="You can refresh insights up to twice every 24 hours"
                    >
                      {refreshMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-4 w-4" />
                      )}
                      {refreshLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span>Last refreshed: {formatRelative((data as any).last_insights_refresh)}</span>
              <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline-block" aria-hidden />
              <span>Total highlights: {(data as any).mom_highlights?.length || 0}</span>
            </div>
            {flash && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 inline-flex items-center justify-between w-full">
                <span>{flash}</span>
                <button onClick={() => setFlash(null)} className="text-emerald-700 font-semibold">Dismiss</button>
              </div>
            )}
            {manageOpen && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 inline-flex items-center justify-between w-full">
                <span>Manage follow-up reminders using the toggle above.</span>
                <button onClick={() => setManageOpen(false)} className="text-slate-700 font-semibold">Close</button>
              </div>
            )}
          </section>

          {/* Midweek Adjust */}
          <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Midweek Adjust</h2>
                <p className="mt-1 text-sm text-slate-600">Quick pass: keep, tweak, or drop this week’s actions.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => (window.location.href = '/wishlist')} className="inline-flex items-center gap-2 rounded-2xl bg-royal-blue px-4 py-2 text-sm font-display font-semibold !text-white shadow-sm transition hover:bg-royal-blue/90">Open Ledger</button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button onClick={() => (window.location.href = '/wishlist')} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">Keep</button>
              <button onClick={() => (window.location.href = '/wishlist')} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">Tweak</button>
              <button onClick={() => (window.location.href = '/wishlist')} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">Drop</button>
            </div>
          </section>

          {/* Weekly Review */}
          <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Weekly Review</h2>
                <p className="mt-1 text-sm text-slate-600">What moved the needle? What will you change next week?</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => (window.location.href = '/wishlist')} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-display font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">Go to Ledger</button>
              </div>
            </div>
            <div className="mt-3">
              <textarea placeholder="Two lines on progress and one change for next week…" className="w-full h-24 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-royal-blue/30" />
            </div>
          </section>

          {/* Action-first sections */}
          {(data as any).insights_v2 ? (
            <InsightsV2
              insightsV2={(data as any).insights_v2}
              onSetFocus={(id) => focusMutation.mutate(id)}
            />
          ) : (
            <section className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
              Generate a MoM to unlock your action plan.
            </section>
          )}

          {/* Archive & Context (Collapsed) */}
          <section className="rounded-3xl border border-slate-200 bg-white/90 p-0 shadow-sm overflow-hidden">
            <details>
              <summary className="list-none cursor-pointer select-none px-6 py-4 text-sm font-semibold text-slate-800 bg-slate-50 border-b border-slate-200">Archive & Context</summary>
              <div className="p-6 space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-xl font-semibold text-slate-900">Recent Aha! Moments</h2>
                    <Sparkles className="h-5 w-5 text-royal-blue" />
                  </div>
                  <div className="mt-4 space-y-4">
                    {((data as any).mom_highlights || []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">Generate a MoM to start capturing breakthrough moments.</div>
                    ) : (
                      (data as any).mom_highlights.map((highlight: any) => (
                        <div key={highlight.session_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="font-display text-lg font-semibold text-slate-900">{highlight.title}</h3>
                            <span className="text-xs text-slate-500">{formatDateTime(highlight.occurred_at)}</span>
                          </div>
                          <ul className="mt-3 space-y-2">
                            {highlight.key_takeaways.slice(0, 3).map((takeaway: string, idx: number) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-royal-blue" />
                                <span>{takeaway}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-xl font-semibold text-slate-900">Journey Timeline</h2>
                    <CalendarDays className="h-5 w-5 text-royal-blue" />
                  </div>
                  <div className="mt-4 space-y-4">
                    {((data as any).session_timeline || []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">Start a coaching session to build your timeline.</div>
                    ) : (
                      (data as any).session_timeline.map((item: any) => (
                        <div key={item.session_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase text-slate-500">{item.coach_type || 'coach'}</p>
                              <h3 className="font-display text-base font-semibold text-slate-900">{item.title}</h3>
                            </div>
                            <span className="text-xs text-slate-500">{formatDateTime(item.occurred_at)}</span>
                          </div>
                          {item.summary && (
                            <p className="mt-2 text-sm text-slate-600">{item.summary}</p>
                          )}
                          {item.key_takeaways.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                              {item.key_takeaways.slice(0, 2).map((point: string, idx: number) => (
                                <li key={idx} className="text-sm text-slate-700">• {point}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </details>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
